import type { ReactElement } from "react";

import styles from "./Select.module.css";

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectProps {
  readonly id: string;
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onChange: (value: string) => void;
  /** An accessible name for a select with no visible `<label>` (from `t()`). */
  readonly label?: string | undefined;
  readonly invalid?: boolean | undefined;
  readonly describedBy?: string | undefined;
  readonly disabled?: boolean | undefined;
}

/**
 * A short list of choices: the native `<select>` styled as the mockup's `.select`.
 *
 * Native on purpose: the platform gives keyboard, type-ahead, screen-reader and mobile pickers for
 * free. A long or searchable list is a `Combobox` instead.
 */
export function Select({
  id,
  value,
  options,
  onChange,
  label,
  invalid = false,
  describedBy,
  disabled = false,
}: SelectProps): ReactElement {
  return (
    <select
      id={id}
      className={styles.select}
      value={value}
      aria-label={label}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      disabled={disabled}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
